import * as LaunchDarkly from "launchdarkly-js-client-sdk";
import { EMPTY, interval, Observable, ReplaySubject } from "rxjs";
import { filter, map, switchMap, take, takeWhile } from "rxjs/operators";
import { CompanyInformationModel } from "../models/account-config.model";
import { environment } from "../../../environments/environment";
import { LaunchDarklyFlags } from "../models/ld-flags.enum";
import { Injectable } from "@angular/core";

@Injectable({
  providedIn: "root",
})
export class LaunchDarklyService {
  private client: LaunchDarkly.LDClient;
  private activeListeners: Partial<
    Record<LaunchDarklyFlags, ReplaySubject<boolean>>
  > = {};
  isInitialized = false;

  initializeLaunchDarkly(companyInfo?: CompanyInformationModel): void {
    if (this.client) {
      return;
    }

    if (!companyInfo) {
      return;
    }

    const account: LaunchDarkly.LDContext = {
      kind: "account",
      name: companyInfo.CompanyName,
      key: `${companyInfo.CompanyID}`,
    };

    if (environment.LAUNCH_DARKLY_CLIENT_ID) {
      this.client = LaunchDarkly.initialize(
        environment.LAUNCH_DARKLY_CLIENT_ID,
        account,
      );
      this.isInitialized = true;
    }
  }

  /**
   * To get the LD flag and it's current status
   * @param flagKey key of the key. Can be found in Launch Darkly UI in corresponding flag, should be defined in enum
   * @param defaultValue  - expected to be false in most cases.
   * @returns An observable that emits the state of the flag once the client is initialized.
   * If the client is not initialized, it waits until the client is ready and then fetches the flag.
   * @NOTE If the flag is not available in current environment - the default value will be returned
   */

  getFlag(
    flagKey: LaunchDarklyFlags,
    defaultValue: LaunchDarkly.LDFlagValue = false,
  ): Observable<LaunchDarkly.LDFlagValue> {
    if (!this.client) {
      return this.waitForClient().pipe(
        switchMap(() => this.getFlag(flagKey, defaultValue)),
      );
    }

    if (!this.activeListeners[flagKey]) {
      this.activeListeners[flagKey] =
        new ReplaySubject<LaunchDarkly.LDFlagValue>(1); // Ensure it stores the last value
      this.client.waitUntilReady().then(() => {
        try {
          const flagValue = this.client.variation(
            flagKey,
          ) as LaunchDarkly.LDFlagValue;
          this.activeListeners[flagKey].next(flagValue);

          // Listen for flag changes
          this.client.on(
            `change:${flagKey}`,
            (newValue: LaunchDarkly.LDFlagValue) => {
              this.activeListeners[flagKey].next(newValue);
            },
          );
        } catch (error) {
          console.error("Error fetching flag value:", error);
          this.activeListeners[flagKey].next(defaultValue);
        }
      });
    }

    return this.activeListeners[flagKey].asObservable();
  }

  waitForClient(): Observable<number> {
    if (this.client) {
      return EMPTY;
    }

    return interval(100).pipe(
      filter(() => Boolean(this.client)),
      take(1),
    );
  }
}
